import React, { useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { CognitoHostedUIIdentityProvider as ssoProviders } from '@aws-amplify/auth';
import { Auth } from 'aws-amplify';
import { useForm, useSubmit } from '@balance-web/forms';

import { IdContext } from '../../../context';
import {
  useMutateCheckSAML,
  useMutateUserSignIn,
} from '../../../services/queryClient';
import { useChallengeUser } from '../useAuthentication/useChallengeUser';
import { events, track } from '../../../utils/analytics';
import { useSingleParam } from '../../../core/Routing';

import { Step, SubmitError, SubmitErrors } from './types';
import { SignInComponentProps } from './types';
import { SignInForm } from './form';
import { formSchema } from './formSchema';

export const SignInComponent = ({
  resetPasswordSuccess,
  resetResetPasswordSuccess,
  onRedirectToClient,
  navigateToResetPassword,
  navigateToSignup,
  navigateToForgotPassword,
  navigateToVerifyMfa,
  navigateToSetupMfa,
}: SignInComponentProps) => {
  const router = useRouter();
  const { tenancy: TENANCY, product, clientId } = useContext(IdContext);
  const checkSaml = useMutateCheckSAML();
  const login = useMutateUserSignIn();
  const { setChallengeUser } = useChallengeUser();

  const [stepValue, setStepValue] = useState<Step>('emailOrUsername');
  const [signInError, setSignInError] = useState<SubmitErrors>();
  const [validatingLogin, setValidatingLogin] = useState(false);
  const unQuery = useSingleParam(router.query.un);
  const ssoQuery = useSingleParam(router.query.sso);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const form = useForm(formSchema(stepValue));
  useEffect(() => {
    if (stepValue === 'login') passwordInputRef.current?.focus();
  }, [stepValue]);

  useEffect(() => {
    if (!unQuery || unQuery !== form.value.emailOrUsername) {
      setStepValue('emailOrUsername');
      form.fields.password.setState({
        value: '',
        touched: false,
      });
    }
    // If field.password is the depency, it will clear itself when an password is typed on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.value.emailOrUsername]);

  useEffect(() => {
    setSignInError(undefined);
  }, [form.value.emailOrUsername, form.value.password]);

  useEffect(() => {
    if (form.value.emailOrUsername) resetResetPasswordSuccess();
  }, [form.value.emailOrUsername, resetResetPasswordSuccess]);

  useEffect(() => {
    if (unQuery) {
      setStepValue('login');
      form.fields.emailOrUsername.setState({
        value: unQuery,
        touched: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unQuery]);

  useEffect(() => {
    if (ssoQuery) {
      if (ssoQuery.toLowerCase() === ssoProviders.Google.toLowerCase()) {
        Auth.federatedSignIn({
          provider: ssoProviders.Google,
          customState: 'li',
        });
      }
      if (ssoQuery.toLowerCase() === 'microsoft') {
        Auth.federatedSignIn({
          customProvider: 'Microsoft',
          customState: 'li',
        });
      }
    }
  }, [ssoQuery]);

  const handleSubmit = useSubmit(form, async (value) => {
    if (stepValue === 'emailOrUsername') {
      checkSaml.mutate(value.emailOrUsername, {
        onSuccess: (provider: string | null) => {
          if (provider)
            Auth.federatedSignIn({
              customProvider: provider,
            });
          else setStepValue('login');
        },
        onError: () => {
          setStepValue('login');
        },
      });
    } else {
      login.mutate(
        {
          emailOrUsername: value.emailOrUsername,
          password: value.password,
        },
        {
          onSuccess: ([user, nextAction]) => {
            setValidatingLogin(true);
            track({
              action: events.USER_LOGGED_IN,
              params: {
                user_id: user?.attributes?.sub,
                source: 'Cognito',
                appId: clientId,
              },
            });
            if (nextAction === 'challenge') setChallengeUser(user);
            else if (nextAction === 'validateMfa') navigateToVerifyMfa();
            else if (nextAction === 'setupMfa') navigateToSetupMfa();
            else onRedirectToClient();
            setValidatingLogin(false);
          },
          onError: (err: any) => {
            if (err?.code === 'PasswordResetRequiredException') {
              navigateToResetPassword();
            }
            if (err?.code === 'UserNotFoundException') {
              setSignInError('Incorrect username or password.');
            } else {
              setSignInError(err?.message);
            }
          },
        }
      );
    }
  });

  const getLoginError = (errorType: SubmitErrors): SubmitError => {
    // These errors are coming from the API
    switch (errorType) {
      case 'Incorrect username or password.':
        return {
          tone: 'critical',
          description:
            'The email or password you’ve entered is incorrect. Please try again.<br /> Your account will be locked after repeated failed attempts.',
        };
      case 'Password attempts exceeded':
        return {
          tone: 'critical',
          description:
            'You have failed to sign in many times, for your safety, your account has been locked.<br/> Please reset your password to unlock your account.',
          actions: {
            primary: {
              label: 'Reset password',
              action: navigateToForgotPassword,
            },
            secondary: {
              label: 'Help with sign in',
              action: () =>
                window.open(process.env.forgotEmailHelpLink as string),
            },
          },
        };
      default:
        return {
          tone: 'critical',
          description: 'Something went wrong. Please try again later.',
        };
    }
  };

  return (
    <SignInForm
      form={form}
      step={stepValue}
      passwordInputRef={passwordInputRef}
      resetPasswordSuccess={resetPasswordSuccess}
      submitAction={{
        loading: checkSaml.isLoading || login.isLoading || validatingLogin,
        onClick: handleSubmit,
        error: signInError && getLoginError(signInError),
        disabled: signInError === 'Password attempts exceeded',
      }}
      isProductProvided={!!product}
      SingleSignOnActions={
        TENANCY !== 'APS'
          ? {
              microsoft: {
                onClick: () =>
                  Auth.federatedSignIn({
                    customProvider: 'Microsoft',
                    customState: 'li',
                  }),
              },
              google: {
                onClick: () =>
                  Auth.federatedSignIn({
                    provider: ssoProviders.Google,
                    customState: 'li',
                  }),
              },
              apple: {
                onClick: () =>
                  Auth.federatedSignIn({
                    provider: ssoProviders.Apple,
                    customState: 'li',
                  }),
              },
            }
          : undefined
      }
      navigation={{
        ...(TENANCY !== 'APS' ? navigateToSignup : null),
        navigateToForgotPassword,
      }}
    />
  );
};
